import config from "@/config.js"
import { defineStore } from "pinia"
import { ref, computed, watch } from "vue"
import { startOfToday } from "date-fns"
import HTTP_STATUS from "@/helpers/httpStatus.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import { MODE } from "@/helpers/filter.js"

export default defineStore("filterStore", () => {
  const currentMeteringPoint = ref(null)
  const currentPortfolio = ref(null)
  const dateRange = ref([startOfToday(), startOfToday()])
  const mode = ref(MODE.BOTH)

  const meteringPointsStore = useMeteringPointsStore()
  const notificationStore = useNotificationStore()

  /**
   * Get all allocation points belonging to the current portfolio.
   */
  const currentPortfolioAllocationPoints = computed(() => {
    const portfolioId = currentPortfolio.value?.id
    return meteringPointsStore.allocationPointsByPortfolio[portfolioId] ?? []
  })

  /**
   * Get all assets belonging to the current portfolio.
   */
  const currentPortfolioAssets = computed(() => {
    const portfolioId = currentPortfolio.value?.id
    return meteringPointsStore.assetsByPortfolio[portfolioId] ?? []
  })

  /**
   * Determine whether the shown results should be pulled from the old 'record
   * streams' API rather than the new 'records' API.
   */
  const useLegacyResults = computed(() => {
    return dateRange.value[0] < config.LEGACY_RESULTS_BREAKPOINT
  })

  function setDateRange(newDateRange) {
    dateRange.value = newDateRange
  }

  function setMode(newMode) {
    if (!Object.values(MODE).includes(newMode)) {
      throw new Error(`Invalid mode: ${newMode}`)
    }
    mode.value = newMode
  }

  function setMeteringPoint(meteringPoint) {
    currentMeteringPoint.value = meteringPoint
  }

  function clearMeteringPoint() {
    currentMeteringPoint.value = null
  }

  function setPortfolio(portfolio, skipClearMeteringPoint = false) {
    currentPortfolio.value = portfolio
    if (!skipClearMeteringPoint) {
      setMeteringPoint(portfolio)
    }
  }

  /**
   * Set the current portfolio to the first portfolio in the list of available
   * portfolios.
   */
  function setDefaultPortfolio() {
    const defaultPortfolio = meteringPointsStore.portfolios[0]
    if (defaultPortfolio) {
      setPortfolio(defaultPortfolio)
    }
  }

  function onLoadFail(error) {
    if (error.code === HTTP_STATUS.CONFLICT) {
      notificationStore.pushError(
        "Fout bij het laden van installaties",
        `De opgehaalde data komt niet overeen. Neem contact op met Repowered. (code: ${error.code})`,
        "load-portfolio-assets-error",
      )
    } else {
      notificationStore.pushError(
        "Fout bij het laden van installaties",
        `De installaties van dit portfolio konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
        "load-portfolio-assets-error",
      )
    }
  }

  /**
   * When the current portfolio changes, clear the current metering point and
   * load the assets for the new portfolio.
   */
  watch(
    currentPortfolio,
    (portfolio) => {
      if (portfolio) {
        portfolio.loadPortfolioDetails(null, onLoadFail)
      }
    },
    { immediate: true },
  )

  return {
    // state
    currentMeteringPoint,
    currentPortfolio,
    dateRange,
    mode,
    // getters
    currentPortfolioAllocationPoints,
    currentPortfolioAssets,
    useLegacyResults,
    // actions
    setDateRange,
    setMode,
    setMeteringPoint,
    clearMeteringPoint,
    setPortfolio,
    setDefaultPortfolio,
  }
})
